// src/page/Dashboard/Dashboard.tsx
import React, { useMemo } from "react";
import Card from "../../../components/Card";
import {
  faGraduationCap,
  faBook,
  faCheck,
  faTasks,
  faTrophy,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faSearch,
  faFilter,
  faTimes,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Modal from "../../../components/Modal/Modal";
import useDashboard from "../useDashboard";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

const COLORS = ["#1e3a8a", "#3b82f6", "#60a5fa", "#93c5fd", "#bfdbfe", "#dbeafe"];
const MEDAL: Record<number, string> = { 1: "🥇", 2: "🥈", 3: "🥉" };

const Dashboard: React.FC = () => {
  const {
    dashboardDetails,
    isModalOpen,
    reportContent,
    setIsModalOpen,
    submissionSummary,
    rankingData,
    rankingLoading,
    rankingRefreshing,
    rankingPagination,
    rankingPage,
    rankingFilters,
    testSeriesOptions,
    languageOptions,
    applyRankingFilter,
    updateNameSearch,
    clearRankingFilters,
    goToPage,
    navigateToAnalysis,
  } = useDashboard();

  const hasActiveFilters =
    !!rankingFilters.name ||
    !!rankingFilters.testSeriesName ||
    !!rankingFilters.languageName;

  const activeFilterCount =
    (rankingFilters.testSeriesName ? 1 : 0) +
    (rankingFilters.languageName ? 1 : 0);

  // ── Card data ───────────────────────────────────────────────────────────
  const cardData = [
    {
      title: "Total Ebook",
      content: String(dashboardDetails?.totalEBooks),
      icon: <FontAwesomeIcon icon={faBook} />,
    },
    {
      title: "Total Test",
      content: String(dashboardDetails?.totalTest),
      icon: <FontAwesomeIcon icon={faCheck} />,
    },
    {
      title: "All Students",
      content: String(dashboardDetails?.allStudents),
      icon: <FontAwesomeIcon icon={faGraduationCap} />,
    },
  ];

  // ── Chart data ──────────────────────────────────────────────────────────
  const chartData = useMemo(() => {
    if (!submissionSummary) return { barData: [], pieData: [] };
    const labels: string[]  = submissionSummary.monthlyData.labels || [];
    const counts: number[]  = submissionSummary.monthlyData.submissionCounts || [];
    const avgScores: number[] = submissionSummary.monthlyData.avgScores || [];

    const barData = labels.map((label, index) => ({
      month:      label.split(" ")[0],
      submissions: counts[index] || 0,
      avgScore:   avgScores[index] ?? 0,
    }));

    const pieData = (submissionSummary.topTestSeries || [])
      .map((t: any) => ({
        name:  t.name.length > 25 ? t.name.substring(0, 25) + "…" : t.name,
        value: Number(t.count || t.totalSubmissions) || 0,
      }))
      .filter((d: any) => d.value > 0);

    return { barData, pieData };
  }, [submissionSummary]);

  // ── Highlight matched text in name ──────────────────────────────────────
  const highlightName = (rawName: string) => {
    const q = rankingFilters.name.trim();
    if (!q) return <span>{rawName}</span>;
    const regex = new RegExp(
      `(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
      "gi"
    );
    const parts = rawName.split(regex);
    return (
      <>
        {parts.map((part, i) =>
          regex.test(part) ? (
            <mark key={i} className="bg-yellow-200 text-gray-900 rounded-sm px-0.5">
              {part}
            </mark>
          ) : (
            <span key={i}>{part}</span>
          )
        )}
      </>
    );
  };


  return (
    <div className="p-4 space-y-6">
      <h1 className="text-2xl text-fontColor font-bold mb-4">Dashboard</h1>

      {/* ─── Top Cards ─────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cardData.map((data, index) => (
          <Card key={index} title={data.title} content={data.content} icon={data.icon} />
        ))}
      </div>

      {/* ─── Submission Overview ─────────────────── */}
      {submissionSummary && (
        <div className="mt-8 border-t border-gray-100 pt-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-fontColor flex items-center gap-2">
              <FontAwesomeIcon icon={faTasks} className="text-blue-800" />
              Test Submission Overview
            </h2>
            <button
              onClick={navigateToAnalysis}
              className="bg-blue-800 hover:bg-blue-900 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              View Full Analysis &rarr;
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            {[
              { label: "Total Submissions", value: submissionSummary.totalSubmissions, color: "blue" },
              { label: "Avg Score",         value: submissionSummary.overallStats.avgScore,     color: "green" },
              { label: "Avg Correct",       value: submissionSummary.overallStats.avgCorrect,   color: "emerald" },
              { label: "Avg Incorrect",     value: submissionSummary.overallStats.avgIncorrect, color: "rose" },
            ].map(({ label, value, color }) => (
              <div
                key={label}
                className={`bg-${color}-50 border border-${color}-100 rounded-xl p-6 shadow-sm flex flex-col items-center justify-center`}
              >
                <div className={`text-2xl font-bold text-${color}-900`}>{value}</div>
                <div className="text-sm font-medium text-gray-500 mt-1">{label}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Bar chart */}
            <div
              className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm group cursor-pointer hover:shadow-md hover:border-blue-200 transition-all relative overflow-hidden"
              onClick={navigateToAnalysis}
            >
              <div className="absolute top-4 right-4 text-xs font-semibold text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity">
                Click for details &rarr;
              </div>
              <h3 className="font-semibold text-sm text-fontColor mb-4">Monthly Submissions (Last 12 Months)</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={chartData.barData} margin={{ top: 10, right: 10, left: -20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#6b7280" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: "#6b7280" }} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{ borderRadius: "8px", border: "1px solid #e5e7eb", fontSize: "12px" }}
                    cursor={{ fill: "#f8fafc" }}
                  />
                  <Bar dataKey="submissions" name="Submissions" fill="#1e3a8a" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Pie chart */}
            <div
              className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm group cursor-pointer hover:shadow-md hover:border-blue-200 transition-all relative overflow-hidden"
              onClick={navigateToAnalysis}
            >
              <div className="absolute top-4 right-4 text-xs font-semibold text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity">
                Click for details &rarr;
              </div>
              <h3 className="font-semibold text-sm text-fontColor mb-4">Top 5 Tests by Submissions</h3>
              <ResponsiveContainer width="100%" height={260}>
                {chartData.pieData.length === 0 ? (
                  <div className="flex justify-center items-center h-full text-gray-400 text-sm">No data available</div>
                ) : (
                  <PieChart>
                    <Pie
                      data={chartData.pieData}
                      cx="50%" cy="50%"
                      outerRadius={75} innerRadius={0}
                      paddingAngle={0}
                      dataKey="value"
                      label={({ percent }: { percent?: number }) =>
                        `${((percent ?? 0) * 100).toFixed(0)}%`
                      }
                      labelLine={false}
                    >
                      {chartData.pieData.map((_: any, index: number) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} stroke="#fff" strokeWidth={2} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ borderRadius: "8px", border: "1px solid #e5e7eb", fontSize: "12px" }}
                      wrapperStyle={{ outline: "none" }}
                    />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: "11px", paddingTop: "10px" }} />
                  </PieChart>
                )}
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* ─── Student Rankings ────────────────────── */}
      <div className="mt-8 border-t border-gray-100 pt-6">

        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-yellow-50 border border-yellow-100">
            <FontAwesomeIcon icon={faTrophy} className="text-yellow-500 text-base" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-fontColor leading-tight">Student Rankings</h2>
            <p className="text-xs text-gray-400 mt-0.5">Ranked by total score across all test submissions</p>
          </div>
          {!rankingLoading && rankingPagination.totalCount > 0 && (
            <span className="ml-auto bg-blue-50 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full border border-blue-100">
              {rankingPagination.totalCount} students
            </span>
          )}
        </div>

        {/* Search + Filter bar */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          {/* Name search */}
          <div className="relative flex-1">
            <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
            <input
              id="ranking-name-search"
              type="text"
              value={rankingFilters.name}
              onChange={(e) => updateNameSearch(e.target.value)}
              placeholder="Search by student name…"
              className="w-full pl-8 pr-8 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition"
            />
            {rankingFilters.name && (
              <button
                onClick={() => updateNameSearch("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
              >
                <FontAwesomeIcon icon={faTimes} className="text-xs" />
              </button>
            )}
          </div>

          {/* Test Series dropdown */}
          <div className="relative">
            <FontAwesomeIcon icon={faFilter} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none" />
            <select
              id="ranking-test-series-filter"
              value={rankingFilters.testSeriesName}
              onChange={(e) => applyRankingFilter("testSeriesName", e.target.value)}
              className="pl-8 pr-8 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition appearance-none min-w-[180px]"
            >
              <option value="">All Test Series</option>
              {testSeriesOptions.map((ts) => (
                <option key={String(ts.id)} value={ts.name}>{ts.name}</option>
              ))}
            </select>
            <FontAwesomeIcon icon={faChevronDown} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none" />
          </div>

          {/* Language dropdown */}
          <div className="relative">
            <FontAwesomeIcon icon={faFilter} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none" />
            <select
              id="ranking-language-filter"
              value={rankingFilters.languageName}
              onChange={(e) => applyRankingFilter("languageName", e.target.value)}
              className="pl-8 pr-8 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition appearance-none min-w-[160px]"
            >
              <option value="">All Languages</option>
              {languageOptions.map((lang) => (
                <option key={lang} value={lang}>{lang}</option>
              ))}
            </select>
            <FontAwesomeIcon icon={faChevronDown} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none" />
          </div>

          {/* Clear all */}
          {hasActiveFilters && (
            <button
              onClick={clearRankingFilters}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-red-500 hover:text-red-700 border border-red-100 hover:border-red-300 rounded-lg bg-red-50 hover:bg-red-100 transition whitespace-nowrap"
            >
              <FontAwesomeIcon icon={faTimes} className="text-xs" />
              Clear
              {activeFilterCount > 0 && (
                <span className="ml-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </button>
          )}
        </div>

        {/* ── Loading skeleton (first load only) ── */}
        {rankingLoading ? (
          <div className="bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm">
            {[...Array(3)].map((_, i) => (
              <div key={i} className={`flex items-center gap-4 px-5 py-4 ${i !== 2 ? "border-b border-gray-50" : ""}`}>
                <div className="w-8 h-5 bg-gray-100 rounded animate-pulse" />
                <div className="w-8 h-8 bg-gray-100 rounded-full animate-pulse" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3.5 bg-gray-100 rounded w-36 animate-pulse" />
                  <div className="h-3 bg-gray-100 rounded w-24 animate-pulse" />
                </div>
                <div className="w-14 h-4 bg-gray-100 rounded animate-pulse" />
                <div className="w-10 h-4 bg-gray-100 rounded animate-pulse" />
              </div>
            ))}
          </div>

        ) : rankingData.length === 0 ? (
          /* ── Empty / no results ── */
          <div className="bg-white border border-gray-100 rounded-xl p-10 shadow-sm flex flex-col items-center justify-center gap-3 text-gray-400">
            <FontAwesomeIcon icon={faTrophy} className="text-4xl text-gray-200" />
            {hasActiveFilters ? (
              <>
                <p className="text-sm font-medium">No students match your search</p>
                <button onClick={clearRankingFilters} className="text-xs text-blue-600 hover:underline">
                  Clear filters
                </button>
              </>
            ) : (
              <>
                <p className="text-sm font-medium">No ranking data available yet</p>
                <p className="text-xs">Rankings will appear once students submit tests</p>
              </>
            )}
          </div>

        ) : (
          /* ── Leaderboard table with refresh overlay ── */
          <div className="relative">
            {/* Subtle overlay while paginating/filtering — keeps table visible */}
            {rankingRefreshing && (
              <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] flex items-center justify-center rounded-xl z-10 pointer-events-none">
                <div className="flex items-center gap-2 bg-white border border-gray-200 shadow-md rounded-full px-4 py-2 text-sm text-gray-600">
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin text-blue-600" />
                  Loading…
                </div>
              </div>
            )}

            <div className="bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm">
              {/* Table header */}
              <div className="grid grid-cols-[52px_1fr_150px_190px_100px_80px] bg-gray-50 border-b border-gray-100 px-5 py-3">
                {["Rank", "Student", "Mobile", "Email", "Score", "Tests"].map((h, i) => (
                  <div
                    key={h}
                    className={`text-xs font-semibold text-gray-400 uppercase tracking-wide ${
                      i >= 4 ? "text-right" : i === 2 ? "hidden md:block" : i === 3 ? "hidden lg:block" : ""
                    }`}
                  >
                    {h}
                  </div>
                ))}
              </div>

              {/* Rows */}
              {rankingData.map((student, idx) => {
                const isTop3 = student.rank <= 3;
                return (
                  <div
                    key={String(student.userId)}
                    className={[
                      "grid grid-cols-[52px_1fr_150px_190px_100px_80px] items-center px-5 py-3.5 transition-colors hover:bg-blue-50/40",
                      idx !== rankingData.length - 1 ? "border-b border-gray-50" : "",
                      isTop3 ? "bg-yellow-50/30" : "",
                    ].join(" ")}
                  >
                    {/* Rank */}
                    <div className="flex items-center">
                      {isTop3 ? (
                        <span className="text-xl leading-none">{MEDAL[student.rank]}</span>
                      ) : (
                        <span className="text-sm font-bold text-gray-400 w-7 text-center">
                          #{student.rank}
                        </span>
                      )}
                    </div>

                    {/* Avatar + Name */}
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className={[
                          "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white",
                          student.rank === 1 ? "bg-yellow-400" :
                          student.rank === 2 ? "bg-gray-400"   :
                          student.rank === 3 ? "bg-amber-600"  : "bg-blue-800",
                        ].join(" ")}
                      >
                        {(student.user.name || "?")[0].toUpperCase()}
                      </div>
                      <span className="text-sm font-semibold text-gray-800 truncate">
                        {highlightName(student.user.name || "—")}
                      </span>
                    </div>

                    {/* Mobile */}
                    <div className="text-sm text-gray-500 hidden md:block truncate">
                      {student.user.mobileNo || "—"}
                    </div>

                    {/* Email */}
                    <div className="text-sm text-gray-500 hidden lg:block truncate">
                      {student.user.email || "—"}
                    </div>

                    {/* Score */}
                    <div className="text-right">
                      <span className="text-sm font-bold text-blue-800">{student.totalScore}</span>
                    </div>

                    {/* Tests */}
                    <div className="text-right">
                      <span className="text-sm text-gray-500">{student.submissionsCount}</span>
                    </div>
                  </div>
                );
              })}

              {/* ── Pagination Footer — always visible ── */}
              <div className="border-t border-gray-100 px-5 py-3 bg-gray-50/60">
                <div className="flex items-center justify-between flex-wrap gap-3">

                  {/* Left: result range */}
                  <span className="text-xs text-gray-400">
                    {rankingPagination.totalCount === 0
                      ? "No results"
                      : (() => {
                          const start = (rankingPage - 1) * rankingPagination.limit + 1;
                          const end = Math.min(
                            rankingPage * rankingPagination.limit,
                            rankingPagination.totalCount
                          );
                          return `Showing ${start}–${end} of ${rankingPagination.totalCount} students`;
                        })()}
                  </span>

                  {/* Right: Prev · pages · Next */}
                  <div className="flex items-center gap-2">
                    {/* ← Previous */}
                    <button
                      id="ranking-prev-btn"
                      disabled={rankingPage <= 1 || rankingRefreshing}
                      onClick={() => goToPage(rankingPage - 1)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 text-gray-600 bg-white hover:bg-gray-50 hover:border-blue-300 hover:text-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                    >
                      <FontAwesomeIcon icon={faChevronLeft} className="text-[10px]" />
                      Previous
                    </button>

                    {/* Page number pills */}
                    <div className="flex items-center gap-1">
                      {Array.from({ length: rankingPagination.totalPages }, (_, i) => i + 1).map((p) => {
                        const nearCurrent = Math.abs(p - rankingPage) <= 1;
                        const isEdge = p === 1 || p === rankingPagination.totalPages;
                        const show = nearCurrent || isEdge;

                        // Ellipsis slots
                        if (!show) {
                          if (p === 2 || p === rankingPagination.totalPages - 1) {
                            return (
                              <span key={`e${p}`} className="text-xs text-gray-400 px-0.5 select-none">
                                …
                              </span>
                            );
                          }
                          return null;
                        }

                        return (
                          <button
                            key={p}
                            onClick={() => goToPage(p)}
                            disabled={rankingRefreshing}
                            className={[
                              "w-7 h-7 flex items-center justify-center rounded-md text-xs font-semibold border transition",
                              rankingPage === p
                                ? "bg-blue-800 text-white border-blue-800 shadow-sm"
                                : "bg-white text-gray-500 border-gray-200 hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300",
                            ].join(" ")}
                          >
                            {p}
                          </button>
                        );
                      })}
                    </div>

                    {/* Next → */}
                    <button
                      id="ranking-next-btn"
                      disabled={rankingPage >= rankingPagination.totalPages || rankingRefreshing}
                      onClick={() => goToPage(rankingPage + 1)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 text-gray-600 bg-white hover:bg-gray-50 hover:border-blue-300 hover:text-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                    >
                      Next
                      <FontAwesomeIcon icon={faChevronRight} className="text-[10px]" />
                    </button>

                    {/* Page indicator */}
                    <span className="text-xs text-gray-400 ml-1 whitespace-nowrap">
                      Page {rankingPage} of {rankingPagination.totalPages || 1}
                    </span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        content={reportContent || []}
      />
    </div>
  );
};

export default Dashboard;
